import React, { useEffect, useState, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useWizard } from "../../context/WizardContext";
import Modal from "./Modal";
import ImageEditor from "./ImageEditor";
import { useAuth } from "../../context/AuthContext";
import LogoLoader from "../common/LogoLoader";
import {
  resolveStoryImageUrl,
  resolveStoryImageUrlOriginal,
  appendCacheBust,
  isPresignedHttpUrl,
} from "../../utils/presignedImageUrl";
import { API_BASE_URL } from "../../config/apiBaseUrl";
import AdminPageNavigator from "../AdminPageNavigator";
import { useTranslation } from "../../i18n/useTranslation";
import { useUiLocale } from "../../context/UiLocaleContext";

type BatchPromptApiPayload = {
  generalInstructions: string;
  pageBlocks: { pageNumber: number; instructions: string }[];
  isSidePicture: boolean;
};

const ImageStudio: React.FC = () => {
  const regenerateFeedbackId = useId().replace(/:/g, "");
  const { 
    sessionId, 
    language, 
    images, 
    storyDescription,
    isLoading,
    error,
    clearError
  } = useWizard();

  const { user, getAuthHeader, token } = useAuth();
  const { t } = useTranslation();
  const { uiLocale } = useUiLocale();

  const navigate = useNavigate();
  const frontendDevMode = process.env.REACT_APP_PRODUCTION_MODE !== 'true';

  // State management - slot-based: each page has 2 slots (main + side picture)
  const [storyPages, setStoryPages] = useState<string[]>([]);
  const [currentSlotIndex, setCurrentSlotIndex] = useState<number>(0);
  const [learnedPeople, setLearnedPeople] = useState<any[]>([]);
  const [pageImages, setPageImages] = useState<any[]>([]);
  const [displaySessionId, setDisplaySessionId] = useState<string | null>(sessionId);
  const [isTraining, setIsTraining] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [trainingPrompts, setTrainingPrompts] = useState<any[]>([]);
  const [currentPagePrompt, setCurrentPagePrompt] = useState<string | null>(null);
  const [isDevelopmentMode, setIsDevelopmentMode] = useState<boolean>(frontendDevMode);
  const [regenerationText, setRegenerationText] = useState<string>('');
  const [showRegenerateInput, setShowRegenerateInput] = useState<boolean>(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isLoadingStory, setIsLoadingStory] = useState<boolean>(true);
  const [isCurrentPromptCopied, setIsCurrentPromptCopied] = useState<boolean>(false);
  const [isIllustrationPromptExpanded, setIsIllustrationPromptExpanded] = useState<boolean>(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchStartStr, setBatchStartStr] = useState("1");
  const [batchEndStr, setBatchEndStr] = useState("1");
  const [batchMainOrSide, setBatchMainOrSide] = useState<"main" | "side">("main");
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchBuiltText, setBatchBuiltText] = useState<string | null>(null);
  const [batchModalError, setBatchModalError] = useState<string | null>(null);
  const [isBatchPromptCopied, setIsBatchPromptCopied] = useState(false);
  const [isEditingImage, setIsEditingImage] = useState<boolean>(false);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [imageRefreshKey, setImageRefreshKey] = useState<number>(0);
  const [justSaved, setJustSaved] = useState<boolean>(false);
  const [resolvedDisplayUrl, setResolvedDisplayUrl] = useState<string | null>(null);
  const [resolvedOriginalUrl, setResolvedOriginalUrl] = useState<string | null>(null);
  const [resolvedMainSidePreviewUrl, setResolvedMainSidePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Session-level text overlay styling preferences (persists across pages)
  const [sessionTextOverlayStyle, setSessionTextOverlayStyle] = useState<any>({
    fontSize: 24,
    color: '#000000',
    alignment: 'center',
    fontFamily: 'Arial',
    outlineColor: '#FFFFFF',
    outlineWidth: 2
  });

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    icon?: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  // Load story data on mount
  useEffect(() => {
    if (!sessionId) {
      navigate("/create/language");
      return;
    }

    if (user?.isAdmin === true) {
      fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(sessionId)}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ adminLastStep: 'image-studio' }),
      }).catch(() => {});
    }

    // Load session-level text overlay styling from localStorage
    const saved = localStorage.getItem(`textOverlayStyle_${sessionId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSessionTextOverlayStyle(parsed);
      } catch (e) {
        console.error('Failed to parse saved text overlay style:', e);
      }
    }

    // Small delay to ensure any previous navigation/state updates are complete
    const timer = setTimeout(() => {
      loadStoryData();
    }, 100);

    return () => clearTimeout(timer);
  }, [sessionId, navigate, user, getAuthHeader, API_BASE_URL]);

  // Auto-open editor when image becomes available after upload (main pictures only)
  // Side pictures open only via the explicit open after upload in handleFileChange
  useEffect(() => {
    if (isUploadingImage) return;
    if (isEditingImage) return;
    if (justSaved) return;
    const isSidePicture = currentSlotIndex % 2 === 1;
    if (isSidePicture) return; // Never auto-open editor for side pictures (avoids reopen after save)
    
    const currentImage = getCurrentPageImage();
    const hasEditedLayer = !!(currentImage?.editedImageUrl || currentImage?.editedImageStorageKey);
    if (currentImage && !hasEditedLayer && !isGenerating) {
      const imageUrl = getImageUrlForEditor();
      if (imageUrl) {
        const timer = setTimeout(() => {
          setIsEditingImage(true);
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [pageImages, currentSlotIndex, isUploadingImage, isEditingImage, justSaved, resolvedDisplayUrl, isGenerating]);

  // Update currentPagePrompt when navigating to a different slot (dev mode)
  useEffect(() => {
    if (isDevelopmentMode && pageImages && pageImages.length > 0) {
      const currentImage = getCurrentPageImage();
      if (currentImage && currentImage.prompt) {
        setCurrentPagePrompt(currentImage.prompt);
        const pageNum = Math.floor(currentSlotIndex / 2) + 1;
        const isSide = currentSlotIndex % 2 === 1;
        console.log('📝 Loaded prompt for page', pageNum, isSide ? '(side)' : '(main)', 'from page image');
      } else {
        // Clear prompt if no page image or no prompt for this slot
        setCurrentPagePrompt(null);
      }
    }
  }, [currentSlotIndex, pageImages, isDevelopmentMode]);

  // Auto-generate prompt in dev mode when navigating to a slot without prompt
  useEffect(() => {
    if (isDevelopmentMode && storyPages.length > 0 && !isGenerating && !isTraining && learnedPeople.length > 0) {
      const currentImage = getCurrentPageImage();
      const hasImage = !!getImageUrlForEditor(false);
      const hasPrompt = !!currentPagePrompt;
      const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
      const isSidePicture = currentSlotIndex % 2 === 1;
      
      // If no image and no prompt, generate the prompt automatically
      // Also generate if image exists but no prompt (for pages that were uploaded before prompt saving was added)
      if (!hasPrompt && pageNumber <= storyPages.length) {
        console.log('📝 Auto-generating prompt for page', pageNumber, isSidePicture ? '(side)' : '(main)', 'in dev mode (hasImage:', hasImage, ')');
        generatePageImage(pageNumber, isSidePicture);
      }
    }
  }, [currentSlotIndex, isDevelopmentMode, storyPages.length, isGenerating, isTraining, learnedPeople.length, resolvedDisplayUrl]);

  // Presigned / sync URLs for current slot (private R2)
  useEffect(() => {
    const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
    const isSidePicture = currentSlotIndex % 2 === 1;
    const img = pageImages.find((i: any) => matchPageImage(i, pageNumber, isSidePicture));
    if (!img) {
      setResolvedDisplayUrl(null);
      setResolvedOriginalUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const [d, o] = await Promise.all([
        resolveStoryImageUrl(img, token),
        resolveStoryImageUrlOriginal(img, token),
      ]);
      if (!cancelled) {
        setResolvedDisplayUrl(d);
        setResolvedOriginalUrl(o);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageImages, currentSlotIndex, imageRefreshKey, token]);

  useEffect(() => {
    const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
    const isSidePicture = currentSlotIndex % 2 === 1;
    if (!isSidePicture || !pageImages.length) {
      setResolvedMainSidePreviewUrl(null);
      return;
    }
    const mainImage = pageImages.find((i: any) => matchPageImage(i, pageNumber, false));
    let cancelled = false;
    (async () => {
      const u = await resolveStoryImageUrl(mainImage, token);
      if (!cancelled) setResolvedMainSidePreviewUrl(u);
    })();
    return () => {
      cancelled = true;
    };
  }, [pageImages, currentSlotIndex, imageRefreshKey, token]);

  const loadStoryData = async () => {
    try {
      // Cache-bust so we always get fresh data when resuming a session
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}?t=${Date.now()}`, {
        headers: { ...getAuthHeader() },
      });
      if (!response.ok) {
        throw new Error('Failed to load story data');
      }
      const data = await response.json();
      const story = data.data;

      // Set development mode based on backend response
      setIsDevelopmentMode(story.generationMode === 'development');
      const currentDevMode = story.generationMode === 'development';

      // Check if we need to analyze characters (production mode only)
      if (!currentDevMode && !story.charactersAnalyzed && story.generatedStory) {
        console.log('🔍 Characters not analyzed yet. Analyzing in production mode...');
        try {
          const analyzeResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}/analyze-characters`, {
            method: 'POST',
            headers: { ...getAuthHeader() },
          });
          
          if (analyzeResponse.ok) {
            await analyzeResponse.json();
            console.log('✅ Character analysis completed');
            // Reload story data to get updated charactersPerPage (fresh fetch, no cache)
            const reloadResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}?t=${Date.now()}`, {
              headers: { ...getAuthHeader() },
            });
            if (reloadResponse.ok) {
              const reloadData = await reloadResponse.json();
              const fresh = reloadData.data;
              if (fresh) {
                Object.assign(story, fresh);
                // Don't overwrite pageImages with undefined if reload response missed them
                if (Array.isArray(fresh.pageImages)) {
                  story.pageImages = fresh.pageImages;
                }
              }
            }
          } else {
            console.warn('⚠️ Failed to analyze characters, continuing with fallback method');
          }
        } catch (err) {
          console.error('Failed to analyze characters:', err);
          // Continue with fallback method
        }
      }

      // After character analysis, check if photo training is needed
      // In production mode: if characters are analyzed but training not done, go to photo training
      if (!currentDevMode && story.charactersAnalyzed && (!story.learnedPeople || story.learnedPeople.length === 0)) {
        console.log('📸 Characters analyzed but photo training not done. Navigating to photo training...');
      navigate("/create/photo-training");
        return;
      }

      if (!story.generatedStory) {
        console.error('❌ No generated story found in ImageStudio. Story data:', {
          sessionId: story.sessionId,
          hasStory: !!story.generatedStory,
          storyLength: story.generatedStory?.length,
          status: story.status,
          allStoryFields: Object.keys(story)
        });
        setIsLoadingStory(false);
        // Don't redirect immediately - show error and let user decide
        showModal(
          'error', 
          'No Story Found', 
          'Please generate a story first before creating images. You will be redirected to the story studio.',
          () => {
            navigate("/create/story-studio");
          },
          'Go to Story Studio',
          '❌'
        );
        return;
      }

      // Parse story into pages
      const pages = parseStoryIntoPages(story.generatedStory, story.language);
      setStoryPages(pages);

      // Always load page images from the server; normalize pageNumber and isSidePicture so main/side match correctly
      const rawPageImages = Array.isArray(story.pageImages) ? story.pageImages : [];
      const normalized = rawPageImages.map((img: any) => ({
        ...img,
        pageNumber: normalizePageNumber(img.pageNumber),
        isSidePicture: normalizeIsSidePicture(img.isSidePicture)
      }));
      setPageImages(normalized);

      // Restore to last slot that has an image so user lands where they left off
      const totalSlots = pages.length * 2;
      let lastSlotWithImage = 0;
      for (let slot = 0; slot < totalSlots; slot++) {
        const pageNum = Math.floor(slot / 2) + 1;
        const isSide = slot % 2 === 1;
        const hasImage = normalized.some((img: any) => {
          const pn = typeof img.pageNumber === 'number' ? img.pageNumber : parseInt(String(img.pageNumber), 10);
          const side = img.isSidePicture === true;
          return (
            pn === pageNum &&
            side === isSide &&
            (img.editedImageUrl ||
              img.editedImageStorageKey ||
              img.uploadedImageUrl ||
              img.uploadedImageStorageKey ||
              img.imageUrl ||
              img.imageStorageKey)
          );
        });
        if (hasImage) lastSlotWithImage = slot;
      }
      setCurrentSlotIndex(lastSlotWithImage);

      if (normalized.length > 0) {
        console.log(
          '📷 Loaded page images on resume:',
          normalized.length,
          normalized.map((img: any) => ({
            page: img.pageNumber,
            side: img.isSidePicture,
            editedImageStorageKey: img.editedImageStorageKey || null,
            uploadedImageStorageKey: img.uploadedImageStorageKey || null,
            imageStorageKey: img.imageStorageKey || null,
          }))
        );
      }

      // In dev mode, load prompt for the restored slot
      if (isDevelopmentMode && normalized.length > 0) {
        const pageNum = Math.floor(lastSlotWithImage / 2) + 1;
        const isSide = lastSlotWithImage % 2 === 1;
        const currentPageImg = normalized.find((img: any) => {
          const pn = typeof img.pageNumber === 'number' ? img.pageNumber : parseInt(String(img.pageNumber), 10);
          return pn === pageNum && img.isSidePicture === isSide;
        });
        if (currentPageImg && currentPageImg.prompt) {
          setCurrentPagePrompt(currentPageImg.prompt);
          console.log('📝 Loaded prompt for page', pageNum, isSide ? '(side)' : '(main)', 'from existing page image');
        }
      }

      // Load learned people if available
      if (story.learnedPeople && story.learnedPeople.length > 0) {
        setLearnedPeople(story.learnedPeople);
        // Generate image for first slot (page 1 main) if not already generated
        const firstPageImage = normalized.find((img: any) =>
          normalizePageNumber(img.pageNumber) === 1 && !normalizeIsSidePicture(img.isSidePicture)
        );
        if (!firstPageImage) {
          generatePageImage(1, false);
        } else if (isDevelopmentMode && firstPageImage.prompt) {
          // In dev mode, if first page image exists with prompt, set it
          setCurrentPagePrompt(firstPageImage.prompt);
        }
      } else {
        // Start training if not done
        // In production mode, this will automatically call OpenAI API
        // In development mode, this will show prompts (but user should have gone through PhotoTraining page)
        if (!isDevelopmentMode) {
          console.log('🔄 Production mode: Auto-triggering training...');
        }
        startTraining();
      }
    } catch (err: any) {
      console.error('Failed to load story data:', err);
      setLocalError(err.message || 'Failed to load story data');
      setIsLoadingStory(false);
    } finally {
      setIsLoadingStory(false);
    }
  };

  const parseStoryIntoPages = (story: string, lang: string | null): string[] => {
    if (!story || story.trim().length === 0) {
      return [];
    }

    const isHebrew = lang === 'Hebrew';
    const pagePattern = isHebrew ? /עמוד\s+\d+\s*:/g : /Page\s+\d+\s*:/gi;
    const parts = story.split(pagePattern);
    const pages = parts.slice(1).map(page => page.trim()).filter(page => page.length > 0);
    
    return pages.length > 0 ? pages : [story.trim()];
  };

  const startTraining = async () => {
    setIsTraining(true);
    clearError();
    setLocalError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/train-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to train images');
      }

      // Check if backend returned development mode
      if (data.data.mode === 'development' || data.data.prompts) {
        setIsDevelopmentMode(true);
        // Development mode: show prompts
        setTrainingPrompts(data.data.prompts || []);
        setLearnedPeople(data.data.learnedPeople || []);
      } else {
        // Production mode: training completed
        setIsDevelopmentMode(false);
        setLearnedPeople(data.data.learnedPeople || []);
      }

      // After training, generate image for first slot (page 1 main) if pages are loaded
      // Use setTimeout to ensure state updates are complete
      setTimeout(() => {
        if (storyPages.length > 0) {
          const firstPageImage = pageImages.find((img: any) => 
            (typeof img.pageNumber === 'number' ? img.pageNumber : parseInt(img.pageNumber)) === 1 && 
            (img.isSidePicture || false) === false
          );
          if (!firstPageImage) {
            generatePageImage(1, false);
          }
        }
      }, 300);
    } catch (err: any) {
      console.error('Training failed:', err);
      setLocalError(err.message || 'Failed to train images');
    } finally {
      setIsTraining(false);
    }
  };

  // Normalize isSidePicture like backend (avoid string "true"/"false" mismatches)
  const normalizeIsSidePicture = (v: any): boolean =>
    v === true || v === 'true' || v === 1 || v === '1';
  const normalizePageNumber = (v: any): number => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    const n = parseInt(String(v), 10);
    return Number.isNaN(n) ? 1 : n;
  };

  const matchPageImage = (img: any, pageNumber: number, isSidePicture: boolean) => {
    if (!img) return false;
    const imgPageNum = normalizePageNumber(img.pageNumber);
    const imgIsSide = normalizeIsSidePicture(img.isSidePicture);
    const targetSide = !!isSidePicture;
    return imgPageNum === pageNumber && imgIsSide === targetSide;
  };

  /** Fresh pageImages only (keeps current slot). Merged server payload restores imageStorageKey for "Edit again". */
  const refreshPageImagesFromServer = async () => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}?t=${Date.now()}`, {
        headers: { ...getAuthHeader() },
      });
      if (!response.ok) return;
      const data = await response.json();
      const story = data.data;
      if (!story) return;
      const rawPageImages = Array.isArray(story.pageImages) ? story.pageImages : [];
      const normalized = rawPageImages.map((img: any) => ({
        ...img,
        pageNumber: normalizePageNumber(img.pageNumber),
        isSidePicture: normalizeIsSidePicture(img.isSidePicture),
      }));
      setPageImages(normalized);
      setImageRefreshKey((k) => k + 1);
    } catch (e) {
      console.error('refreshPageImagesFromServer failed', e);
    }
  };

  const openImageEditorWithFreshPageImagesIfNeeded = async () => {
    const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
    const isSidePicture = currentSlotIndex % 2 === 1;
    const img =
      pageImages?.find((i: any) => matchPageImage(i, pageNumber, isSidePicture)) || null;
    const hasEdited = !!(img?.editedImageUrl || img?.editedImageStorageKey);
    if (hasEdited) {
      await refreshPageImagesFromServer();
    }
    setIsEditingImage(true);
  };

  const generatePageImage = async (pageNumber: number, isSidePicture: boolean = false) => {
    setIsGenerating(true);
    clearError();
    setLocalError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/generate-page-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ pageNumber, isSidePicture }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate image');
      }

      // Check if backend returned development mode
      if (data.data.mode === 'development' || data.data.prompt) {
        setIsDevelopmentMode(true);
        // Development mode: show prompt
        setCurrentPagePrompt(data.data.prompt);
      } else if (data.data.pageImage) {
        setIsDevelopmentMode(false);
        // Production mode: image generated
        const updatedPageImages = [...pageImages];
        const existingIndex = updatedPageImages.findIndex(img => matchPageImage(img, pageNumber, isSidePicture));
        
        if (existingIndex >= 0) {
          updatedPageImages[existingIndex] = data.data.pageImage;
        } else {
          updatedPageImages.push(data.data.pageImage);
        }
        setPageImages(updatedPageImages);
        
        // Automatically open editor for production mode images
        setTimeout(() => {
          setIsEditingImage(true);
        }, 500);
      }
    } catch (err: any) {
      console.error('Image generation failed:', err);
      setLocalError(err.message || 'Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
    const isSidePicture = currentSlotIndex % 2 === 1;
    setIsGenerating(true);
    clearError();
    setLocalError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/regenerate-page-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ 
          pageNumber,
          modificationText: regenerationText.trim() || undefined,
          isSidePicture
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to regenerate image');
      }

      // Check if backend returned development mode
      if (data.data.mode === 'development' || data.data.prompt) {
        setIsDevelopmentMode(true);
        // Development mode: show prompt
        setCurrentPagePrompt(data.data.prompt);
      } else if (data.data.pageImage) {
        setIsDevelopmentMode(false);
        // Production mode: image regenerated
        const updatedPageImages = [...pageImages];
        const existingIndex = updatedPageImages.findIndex(img => matchPageImage(img, pageNumber, isSidePicture));
        
        if (existingIndex >= 0) {
          updatedPageImages[existingIndex] = data.data.pageImage;
        } else {
          updatedPageImages.push(data.data.pageImage);
        }
        setPageImages(updatedPageImages);
        
        // Automatically open editor for regenerated images
        setTimeout(() => {
          setIsEditingImage(true);
        }, 500);
      }

      setRegenerationText('');
      setShowRegenerateInput(false);
    } catch (err: any) {
      console.error('Image regeneration failed:', err);
      setLocalError(err.message || 'Failed to regenerate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async () => {
    const totalSlots = storyPages.length * 2;
    const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
    const isSidePicture = currentSlotIndex % 2 === 1;

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/approve-page-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ pageNumber, isSidePicture }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to approve image');
      }

      // Update local state
      const updatedPageImages = [...pageImages];
      const existingIndex = updatedPageImages.findIndex(img => matchPageImage(img, pageNumber, isSidePicture));
      
      if (existingIndex >= 0) {
        updatedPageImages[existingIndex].approved = true;
      }
      setPageImages(updatedPageImages);

      // Move to next slot if available
      if (currentSlotIndex < totalSlots - 1) {
        setCurrentSlotIndex(currentSlotIndex + 1);
        const nextPageNumber = Math.floor((currentSlotIndex + 1) / 2) + 1;
        const nextIsSidePicture = (currentSlotIndex + 1) % 2 === 1;
        const nextExistingImage = pageImages.find(img => matchPageImage(img, nextPageNumber, nextIsSidePicture) && img.approved);
        if (!nextExistingImage) {
          generatePageImage(nextPageNumber, nextIsSidePicture);
        }
      } else {
        showModal('success', 'All Pages Complete!', 'You have approved images for all pages!', undefined, 'Great!', '🎉');
      }
    } catch (err: any) {
      console.error('Approval failed:', err);
      setLocalError(err.message || 'Failed to approve image');
    }
  };

  const handleNextPage = () => {
    const totalSlots = storyPages.length * 2;
    if (currentSlotIndex < totalSlots - 1) {
      setIsEditingImage(false); // Close editor when navigating
      const nextSlot = currentSlotIndex + 1;
      setCurrentSlotIndex(nextSlot);
      const nextPageNumber = Math.floor(nextSlot / 2) + 1;
      const nextIsSidePicture = nextSlot % 2 === 1;
      const existingImage = pageImages.find(img => matchPageImage(img, nextPageNumber, nextIsSidePicture) && img.approved);
      
      if (!existingImage) {
        generatePageImage(nextPageNumber, nextIsSidePicture);
      }
    } else {
      // On the last slot, navigate to Cover Studio
      setIsEditingImage(false);
      console.log('Navigating to Cover Studio...');
      // Admin guarantee: flush progress before moving forward.
      if (user?.isAdmin === true && sessionId) {
        fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(sessionId)}/progress`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify({ adminLastStep: 'cover-studio' }),
        }).catch(() => {});
      }
      navigate("/create/cover-studio");
    }
  };

  const handlePreviousPage = () => {
    if (currentSlotIndex > 0) {
      setIsEditingImage(false); // Close editor when navigating
      const prevSlot = currentSlotIndex - 1;
      setCurrentSlotIndex(prevSlot);
      const prevPageNumber = Math.floor(prevSlot / 2) + 1;
      const prevIsSidePicture = prevSlot % 2 === 1;
      generatePageImage(prevPageNumber, prevIsSidePicture);
    }
  };

  const showModal = (
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm',
    title: string,
    message: string,
    onConfirm?: () => void,
    confirmText?: string,
    icon?: string
  ) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      confirmText,
      icon
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    closeModal();
  };

  const getCurrentPageImage = () => {
    const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
    const isSidePicture = currentSlotIndex % 2 === 1;
    if (!pageImages || pageImages.length === 0) {
      return null;
    }
    return pageImages.find((img: any) => matchPageImage(img, pageNumber, isSidePicture)) || null;
  };

  const isCurrentPageApproved = () => {
    const pageImage = getCurrentPageImage();
    return pageImage?.approved || false;
  };

  const getImageUrlForEditor = (useOriginal: boolean = false) => {
    const pageImage = getCurrentPageImage();
    if (!pageImage) {
      return null;
    }

    // If useOriginal is true (Edit again): only uploaded / generated — never fall through to edited
    // (fall-through used to return resolvedDisplayUrl = edited presigned URL → R2 CORS on canvas).
    if (useOriginal) {
      if (pageImage.uploadedImageUrl) {
        let url = pageImage.uploadedImageUrl.startsWith('http')
          ? pageImage.uploadedImageUrl
          : `${API_BASE_URL.replace(/\/api\/?$/, '')}${pageImage.uploadedImageUrl}`;
        url = appendCacheBust(url, imageRefreshKey);
        return url;
      }
      if (pageImage.imageUrl) {
        let url = pageImage.imageUrl;
        if (!url.startsWith('blob:') && !url.startsWith('data:') && !url.startsWith('http')) {
          url = `${API_BASE_URL.replace(/\/api\/?$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
        }
        if (!url.startsWith('blob:') && !url.startsWith('data:')) {
          url = appendCacheBust(url, imageRefreshKey);
        }
        return url;
      }
      if (pageImage.uploadedImageStorageKey || pageImage.imageStorageKey) {
        return null;
      }
      if (resolvedOriginalUrl && !isPresignedHttpUrl(resolvedOriginalUrl)) {
        return appendCacheBust(resolvedOriginalUrl, imageRefreshKey);
      }
      return null;
    }

    // Normal flow: prefer edited image (saved with text), then uploaded, then generated (main and side)
    if (pageImage.editedImageUrl) {
      let url = pageImage.editedImageUrl.startsWith('blob:') || pageImage.editedImageUrl.startsWith('http')
        ? pageImage.editedImageUrl
        : `${API_BASE_URL.replace(/\/api\/?$/, '')}${pageImage.editedImageUrl}`;
      if (!url.startsWith('blob:')) {
        url = appendCacheBust(url, imageRefreshKey);
      }
      return url;
    }
    if (pageImage.editedImageStorageKey) {
      if (resolvedDisplayUrl) {
        return appendCacheBust(resolvedDisplayUrl, imageRefreshKey);
      }
      return null;
    }
    if (pageImage.uploadedImageUrl) {
      let url = pageImage.uploadedImageUrl.startsWith('http')
        ? pageImage.uploadedImageUrl
        : `${API_BASE_URL.replace(/\/api\/?$/, '')}${pageImage.uploadedImageUrl}`;
      url = appendCacheBust(url, imageRefreshKey);
      return url;
    }
    if (pageImage.uploadedImageStorageKey && !pageImage.uploadedImageUrl) {
      if (resolvedDisplayUrl) {
        return appendCacheBust(resolvedDisplayUrl, imageRefreshKey);
      }
      return null;
    }
    if (pageImage.imageUrl) {
      let url = pageImage.imageUrl;
      if (!url.startsWith('blob:') && !url.startsWith('data:') && !url.startsWith('http')) {
        url = `${API_BASE_URL.replace(/\/api\/?$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
      }
      if (!url.startsWith('blob:') && !url.startsWith('data:')) {
        url = appendCacheBust(url, imageRefreshKey);
      }
      return url;
    }
    if (pageImage.imageStorageKey && !pageImage.imageUrl) {
      if (resolvedDisplayUrl) {
        return appendCacheBust(resolvedDisplayUrl, imageRefreshKey);
      }
      return null;
    }
    if (resolvedDisplayUrl) {
      return appendCacheBust(resolvedDisplayUrl, imageRefreshKey);
    }
    return null;
  };

  /** R2 key for ImageEditor canvas load (avoids CORS on presigned S3 URLs). */
  const getStorageKeyForImageEditor = (useOriginal: boolean): string | null => {
    const pageImage = getCurrentPageImage();
    if (!pageImage) return null;
    if (useOriginal) {
      if (pageImage.uploadedImageStorageKey) return pageImage.uploadedImageStorageKey;
      if (pageImage.imageStorageKey) return pageImage.imageStorageKey;
      return null;
    }
    if (pageImage.editedImageStorageKey) return pageImage.editedImageStorageKey;
    if (pageImage.uploadedImageStorageKey) return pageImage.uploadedImageStorageKey;
    if (pageImage.imageStorageKey) return pageImage.imageStorageKey;
    return null;
  };

  const handleUploadImage = () => {
    fileInputRef.current?.click();
  };

  const handleCopyCurrentPrompt = async () => {
    if (!currentPagePrompt) return;
    try {
      await navigator.clipboard.writeText(currentPagePrompt);
      setIsCurrentPromptCopied(true);
      window.setTimeout(() => setIsCurrentPromptCopied(false), 2000);
    } catch {
      setLocalError('Failed to copy prompt to clipboard');
    }
  };

  const assembleBatchPromptFromPayload = (data: BatchPromptApiPayload): string => {
    const count = data.pageBlocks.length;
    const lines: string[] = [];
    lines.push(t("imageStudio.batch.intro", { count }));
    lines.push("");
    lines.push(t("imageStudio.batch.separatorGeneral"));
    lines.push(data.generalInstructions);
    const kindLabel = data.isSidePicture
      ? t("imageStudio.batch.kindSide")
      : t("imageStudio.batch.kindMain");
    for (const block of data.pageBlocks) {
      lines.push("");
      lines.push(t("imageStudio.batch.separatorPage", { page: block.pageNumber, kind: kindLabel }));
      lines.push(block.instructions);
    }
    return lines.join("\n");
  };

  const openBatchPromptModal = () => {
    const maxP = storyPages.length || 1;
    const cur = Math.floor(currentSlotIndex / 2) + 1;
    const clamped = Math.min(Math.max(cur, 1), maxP);
    setBatchStartStr(String(clamped));
    setBatchEndStr(String(clamped));
    setBatchMainOrSide(currentSlotIndex % 2 === 1 ? "side" : "main");
    setBatchBuiltText(null);
    setBatchModalError(null);
    setIsBatchPromptCopied(false);
    setBatchModalOpen(true);
  };

  const buildBatchPrompt = async () => {
    setBatchModalError(null);
    const maxP = storyPages.length;
    if (!sessionId || !maxP) {
      setBatchModalError(t("imageStudio.batch.buildError"));
      return;
    }
    const start = parseInt(batchStartStr, 10);
    const end = parseInt(batchEndStr, 10);
    if (Number.isNaN(start) || Number.isNaN(end)) {
      setBatchModalError(t("imageStudio.batch.hintRange", { max: maxP }));
      return;
    }
    if (start < 1 || end > maxP) {
      setBatchModalError(t("imageStudio.batch.hintRange", { max: maxP }));
      return;
    }
    if (start > end) {
      setBatchModalError(t("imageStudio.batch.validationOrder"));
      return;
    }
    if (end - start + 1 > 8) {
      setBatchModalError(t("imageStudio.batch.validationMax"));
      return;
    }
    setBatchLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/page-image-batch-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({
          startPage: start,
          endPage: end,
          isSidePicture: batchMainOrSide === "side",
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || t("imageStudio.batch.buildError"));
      }
      const payload = data.data as BatchPromptApiPayload;
      setBatchBuiltText(assembleBatchPromptFromPayload(payload));
    } catch (e: unknown) {
      setBatchModalError(e instanceof Error ? e.message : t("imageStudio.batch.buildError"));
    } finally {
      setBatchLoading(false);
    }
  };

  const handleCopyBatchBuiltPrompt = async () => {
    if (!batchBuiltText) return;
    try {
      await navigator.clipboard.writeText(batchBuiltText);
      setIsBatchPromptCopied(true);
      window.setTimeout(() => setIsBatchPromptCopied(false), 2000);
    } catch {
      setBatchModalError(t("imageStudio.batch.clipboardError"));
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setLocalError('Please select an image file');
      return;
    }

    setIsUploadingImage(true);
    setLocalError(null);

    try {
      const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
      const isSidePicture = currentSlotIndex % 2 === 1;
      const formData = new FormData();
      formData.append('image', file);
      formData.append('pageNumber', pageNumber.toString());
      formData.append('isSidePicture', isSidePicture.toString());

      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/upload-page-image`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload image');
      }

      // Update local state
      if (!data.data || !data.data.pageImage) {
        throw new Error('Invalid response from server: missing pageImage');
      }

      const updatedPageImages = [...(pageImages || [])];
      const existingIndex = updatedPageImages.findIndex(img => img && matchPageImage(img, pageNumber, isSidePicture));
      
      // Merge server response and ensure edited fields are cleared for this upload
      // (server clears them for side; for main we also want new upload = no edited version yet)
      const mergedPageImage = {
        ...(existingIndex >= 0 ? updatedPageImages[existingIndex] : {}),
        ...data.data.pageImage,
        editedImageUrl: undefined,
        editedImagePath: undefined,
        textOverlay: undefined
      };
      
      if (existingIndex >= 0) {
        updatedPageImages[existingIndex] = mergedPageImage;
      } else {
        updatedPageImages.push(mergedPageImage);
      }
      
      console.log('Updated pageImages:', updatedPageImages);
      setPageImages(updatedPageImages);
      
      // For side pictures, remember that in this visit we now have an uploaded side image
      if (isSidePicture) {
        setHasSideUploadThisVisit(true);
      }
      
      // Open editor after upload so user can add text and save (same flow as main images)
      setTimeout(() => {
        setIsEditingImage(true);
      }, 400);
    } catch (err: any) {
      console.error('Image upload failed:', err);
      setLocalError(err.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSaveImageEdit = async (editedImageUrl: string, textOverlay: any) => {
    try {
      // Convert blob URL to blob
      const response = await fetch(editedImageUrl);
      const blob = await response.blob();

      const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
      const isSidePicture = currentSlotIndex % 2 === 1;
      const formData = new FormData();
      formData.append('editedImage', blob, `page-${pageNumber}-${isSidePicture ? 'side' : 'main'}-edited.png`);
      formData.append('pageNumber', pageNumber.toString());
      formData.append('isSidePicture', isSidePicture.toString());
      formData.append('textOverlay', JSON.stringify(textOverlay));

      const saveResponse = await fetch(`${API_BASE_URL}/stories/${sessionId}/save-image-edit`, {
        method: 'POST',
        headers: { ...getAuthHeader() },
        body: formData,
      });

      const data = await saveResponse.json();

      if (!saveResponse.ok) {
        throw new Error(data.message || 'Failed to save edited image');
      }

      // Update local state
      if (!data.data || !data.data.pageImage) {
        throw new Error('Invalid response from server: missing pageImage');
      }

      // Normalize pageNumber for comparison
      const pageNumberNum = typeof pageNumber === 'number' ? pageNumber : parseInt(pageNumber);
      
      // Update state using functional update to ensure we work with latest state
      setPageImages((prevPageImages) => {
        const updatedPageImages = [...(prevPageImages || [])];
        const existingIndex = updatedPageImages.findIndex(img => matchPageImage(img, pageNumberNum, isSidePicture));
        
        if (existingIndex >= 0) {
          // Preserve the approved status from the existing image if the response doesn't have it
          const existingApproved = updatedPageImages[existingIndex]?.approved;
          // Completely replace with the new data from server but keep slot identity so we don't show the wrong image
          const newPageImage = { 
            ...data.data.pageImage,
            pageNumber: pageNumberNum,
            isSidePicture: isSidePicture,
            approved: data.data.pageImage?.approved !== undefined ? data.data.pageImage.approved : existingApproved
          };
          // Replace the existing image with the new one
          updatedPageImages[existingIndex] = newPageImage;
        } else {
          const newPageImage = {
            ...data.data.pageImage,
            pageNumber: pageNumberNum,
            isSidePicture: isSidePicture
          };
          updatedPageImages.push(newPageImage);
        }
        
        console.log('📝 Setting pageImages state with:', updatedPageImages.map(img => ({
          pageNumber: img?.pageNumber,
          editedImageUrl: img?.editedImageUrl,
          hasTextOverlay: !!img?.textOverlay
        })));
        
        return updatedPageImages;
      });
      
      // Increment refresh key to force image re-render with updated URL
      setImageRefreshKey(prev => prev + 1);

      // Save session-level text overlay styling preferences to localStorage
      // Extract styling properties from textOverlay (excluding content and position which are page-specific)
      if (textOverlay) {
        const updatedSessionStyle = {
          fontSize: textOverlay.fontSize ?? sessionTextOverlayStyle.fontSize ?? 24,
          color: textOverlay.color ?? sessionTextOverlayStyle.color ?? '#000000',
          alignment: textOverlay.alignment ?? sessionTextOverlayStyle.alignment ?? 'center',
          fontFamily: textOverlay.fontFamily ?? sessionTextOverlayStyle.fontFamily ?? 'Arial',
          outlineColor: textOverlay.outlineColor ?? sessionTextOverlayStyle.outlineColor ?? '#FFFFFF',
          outlineWidth: textOverlay.outlineWidth ?? sessionTextOverlayStyle.outlineWidth ?? 2
        };
        setSessionTextOverlayStyle(updatedSessionStyle);
        localStorage.setItem(`textOverlayStyle_${sessionId}`, JSON.stringify(updatedSessionStyle));
        console.log('💾 Saved session text overlay style:', updatedSessionStyle);
      }

      // Close editor to show the saved image
      setIsEditingImage(false);
      
      // Set flag to prevent auto-opening editor immediately after save
      setJustSaved(true);
      setTimeout(() => {
        setJustSaved(false);
      }, 2000); // Clear flag after 2 seconds

      // Show success message
      showModal('success', 'Image Saved!', 'Your edited image with text overlay has been saved successfully.', undefined, 'Great!', '✅');
    } catch (err: any) {
      console.error('Failed to save edited image:', err);
      setLocalError(err.message || 'Failed to save edited image');
    }
  };

  const handleResetImageEdit = async () => {
    try {
      const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
      const isSidePicture = currentSlotIndex % 2 === 1;
      
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/reset-image-edit/${pageNumber}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ isSidePicture }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset edited image');
      }

      // Update local state with the response
      const updatedPageImages = [...(pageImages || [])];
      const existingIndex = updatedPageImages.findIndex(img => img && matchPageImage(img, pageNumber, isSidePicture));
      
      if (existingIndex >= 0 && data.data && data.data.pageImage) {
        updatedPageImages[existingIndex] = { ...updatedPageImages[existingIndex], ...data.data.pageImage };
      }
      
      setPageImages(updatedPageImages);
      
      // Increment refresh key to force image re-render
      setImageRefreshKey(prev => prev + 1);

      showModal('success', 'Image Reset!', 'The edited image has been reset to the original. You can now edit it again.', undefined, 'OK', '✅');
    } catch (err: any) {
      console.error('Failed to reset edited image:', err);
      setLocalError(err.message || 'Failed to reset edited image');
    }
  };

  const handleRemoveImage = async () => {
    const pageNumber = Math.floor(currentSlotIndex / 2) + 1;
    const isSidePicture = currentSlotIndex % 2 === 1;
    const pageNumberNum = typeof pageNumber === 'number' ? pageNumber : parseInt(pageNumber);
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/remove-page-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ pageNumber, isSidePicture }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to remove image');
      }
      setIsEditingImage(false);
      if (isSidePicture) {
        setHasSideUploadThisVisit(false);
      }
      // Always clear image fields in state so UI shows prompt again (JSON may omit undefined from response)
      let promptToShow: string | null = null;
      setPageImages((prev) => {
        const updated = [...(prev || [])];
        const idx = updated.findIndex((img: any) => matchPageImage(img, pageNumberNum, isSidePicture));
        if (idx >= 0) {
          const existing = updated[idx];
          promptToShow = existing.prompt ?? data.data?.pageImage?.prompt ?? null;
          updated[idx] = {
            ...existing,
            pageNumber: pageNumberNum,
            isSidePicture,
            prompt: existing.prompt ?? data.data?.pageImage?.prompt,
            pageContent: existing.pageContent ?? data.data?.pageImage?.pageContent,
            imageUrl: undefined,
            imageStorageKey: undefined,
            uploadedImageUrl: undefined,
            uploadedImagePath: undefined,
            uploadedImageStorageKey: undefined,
            editedImageUrl: undefined,
            editedImagePath: undefined,
            editedImageStorageKey: undefined,
            textOverlay: undefined,
            approved: false
          };
        }
        return updated;
      });
      if (promptToShow != null) {
        setCurrentPagePrompt(promptToShow);
      }
      setImageRefreshKey((k) => k + 1);
      showModal('success', 'Image removed', 'The image has been removed. You can upload or generate a new one.', undefined, 'OK', '✅');
    } catch (err: any) {
      console.error('Failed to remove image:', err);
      setLocalError(err.message || 'Failed to remove image');
    }
  };

  const totalSlots = storyPages.length * 2;
  const currentPageNumber = Math.floor(currentSlotIndex / 2) + 1;
  const currentIsSidePicture = currentSlotIndex % 2 === 1;
  const currentPageContent = storyPages[Math.floor(currentSlotIndex / 2)] || '';
  const currentPageImage = getCurrentPageImage();
  /** R2 often has editedImageStorageKey only (no editedImageUrl) — must match for "edit again" / Next gating. */
  const hasSavedEditedLayer =
    !!(currentPageImage?.editedImageUrl || currentPageImage?.editedImageStorageKey);
  /** Match props passed to ImageEditor: original base when re-editing, else display stack. */
  const editorUseOriginal = isEditingImage && hasSavedEditedLayer;
  const imageEditorCanLoad =
    !!getImageUrlForEditor(editorUseOriginal) ||
    !!getStorageKeyForImageEditor(editorUseOriginal);

  // Does the current (side) slot have an image? If yes, show it; if no, show prompt + upload.
  const hasSideImage =
    !!currentPageImage &&
    !!(
      currentPageImage.editedImageUrl ||
      currentPageImage.editedImageStorageKey ||
      currentPageImage.uploadedImageUrl ||
      currentPageImage.uploadedImageStorageKey ||
      currentPageImage.imageUrl ||
      currentPageImage.imageStorageKey
    );

  // Track whether user has uploaded a side image in the current slot (so we switch to big image immediately)
  const [hasSideUploadThisVisit, setHasSideUploadThisVisit] = useState(false);
  useEffect(() => {
    setHasSideUploadThisVisit(false);
  }, [currentSlotIndex]);

  // In development mode, for a side picture: show prompt only when the slot has NO image yet.
  // Once there is an image (uploaded or saved), show the big image so it persists on Next/Back.
  const shouldShowSidePrompt =
    isDevelopmentMode &&
    currentIsSidePicture &&
    !!currentPagePrompt &&
    !isEditingImage &&
    !isGenerating &&
    !hasSideImage &&
    !hasSideUploadThisVisit;

  // Helper: build a URL for displaying the MAIN page edited image as reference
  // for side-picture background generation (always use the saved edited image if available).
  const getMainImageUrlForSidePreview = () => {
    if (!currentIsSidePicture || !pageImages || pageImages.length === 0) return null;
    if (resolvedMainSidePreviewUrl) {
      return appendCacheBust(resolvedMainSidePreviewUrl, imageRefreshKey);
    }
    const mainImage = pageImages.find((img: any) => matchPageImage(img, currentPageNumber, false));
    if (!mainImage) return null;

    let baseUrl: string | null = null;

    if (mainImage.editedImageUrl) {
      baseUrl = mainImage.editedImageUrl;
    } else if (mainImage.imageUrl) {
      baseUrl = mainImage.imageUrl;
    } else if (mainImage.uploadedImageUrl) {
      baseUrl = mainImage.uploadedImageUrl;
    }

    if (!baseUrl) return null;

    const url = baseUrl.startsWith('http')
      ? baseUrl
      : `${API_BASE_URL.replace(/\/api\/?$/, '')}${baseUrl}`;
    return appendCacheBust(url, imageRefreshKey);
  };
  const slotLabel = currentIsSidePicture 
    ? (language === 'Hebrew' ? `עמוד ${currentPageNumber} - תמונת צד` : `Page ${currentPageNumber} - Side Picture`)
    : (language === 'Hebrew' ? `עמוד ${currentPageNumber}` : `Page ${currentPageNumber} - Main`);

  return (
    <div 
      className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex flex-col items-center px-4 pt-8"
      style={{
        backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/3_background.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <main
        id="main-content"
        tabIndex={-1}
        className="relative flex w-full max-w-4xl flex-1 flex-col items-center outline-none"
      >
      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 max-w-4xl w-full">
          <span className="block sm:inline">{error || localError}</span>
          <button
            onClick={() => {
              clearError();
              setLocalError(null);
            }}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 pb-24 shadow-card border border-white/40 max-w-4xl w-full z-10">
        <h1 className="text-3xl font-extrabold text-heading mb-3 text-center">
          Image Generation Studio 🎨
        </h1>
        <AdminPageNavigator align="center" />

        {user?.isAdmin === true && storyPages.length > 0 && (
          <div className="mb-4 flex justify-center">
            <button
              type="button"
              onClick={openBatchPromptModal}
              className="inline-flex items-center justify-center rounded-xl border-0 bg-blue-600 px-6 py-3 text-sm font-bold text-white shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-lg active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
            >
              {t("imageStudio.batch.openButton")}
            </button>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{slotLabel} ({currentSlotIndex + 1} of {totalSlots})</span>
            <span>{totalSlots > 0 ? Math.round(((currentSlotIndex + 1) / totalSlots) * 100) : 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="h-3 rounded-full transition-all duration-500 bg-purple-500"
              style={{ width: `${totalSlots > 0 ? ((currentSlotIndex + 1) / totalSlots) * 100 : 0}%` }}
            ></div>
          </div>
        </div>

        {/* Jump to page / Beginning / End */}
        {totalSlots > 0 && (
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentSlotIndex(0)}
              disabled={currentSlotIndex === 0}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 transition"
              title={language === 'Hebrew' ? 'להתחלה' : 'Go to beginning'}
            >
              {language === 'Hebrew' ? '⏮ התחלה' : '⏮ Beginning'}
            </button>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              {language === 'Hebrew' ? 'עמוד:' : 'Jump to:'}
              <select
                value={currentSlotIndex}
                onChange={(e) => setCurrentSlotIndex(Number(e.target.value))}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                aria-label={language === 'Hebrew' ? 'בחר עמוד' : 'Select page'}
              >
                {Array.from({ length: totalSlots }, (_, i) => {
                  const pageNum = Math.floor(i / 2) + 1;
                  const isSide = i % 2 === 1;
                  const label = isSide
                    ? (language === 'Hebrew' ? `עמוד ${pageNum} - צד` : `Page ${pageNum} (side)`)
                    : (language === 'Hebrew' ? `עמוד ${pageNum} - ראשי` : `Page ${pageNum} (main)`);
                  return (
                    <option key={i} value={i}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </label>
            <button
              type="button"
              onClick={() => setCurrentSlotIndex(totalSlots - 1)}
              disabled={currentSlotIndex === totalSlots - 1}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 transition"
              title={language === 'Hebrew' ? 'לסוף' : 'Go to end'}
            >
              {language === 'Hebrew' ? 'סוף ⏭' : 'End ⏭'}
            </button>
          </div>
        )}
        <div className="mb-6 flex justify-center">
          <button
            onClick={() =>
              navigate(
                sessionId
                  ? `/create/result?sessionId=${encodeURIComponent(sessionId)}`
                  : "/create/result"
              )
            }
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-8 py-3 rounded-2xl text-lg shadow-soft hover:shadow-card transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2"
          >
            Skip to Current Digital Book →
          </button>
        </div>

        {/* Training Step (Development Mode) */}
        {isDevelopmentMode && trainingPrompts.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-blue-800 mb-4">📝 Training Prompts (Development Mode)</h2>
            <div className="space-y-4">
              {trainingPrompts.map((promptData, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-700">
                      Image {index + 1}: {(() => {
                        if (!promptData.tags) return 'No information';
                        const nameMatch = promptData.tags.match(/name:\s*([^,]+)/i);
                        const roleMatch = promptData.tags.match(/role:\s*([^,]+)/i);
                        const name = nameMatch ? nameMatch[1].trim() : '';
                        const role = roleMatch ? roleMatch[1].trim() : '';
                        if (name && role) return `${name} (${role})`;
                        if (name) return name;
                        if (role) return role;
                        return promptData.tags;
                      })()}
                    </h3>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(promptData.prompt);
                        alert('Prompt copied to clipboard!');
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 p-3 rounded">
                    {promptData.prompt}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Training Loading */}
        {isTraining && (
          <div className="mb-6 p-6 bg-blue-50 rounded-lg text-center">
            <LogoLoader size="md" className="mb-4" />
            <p className="text-blue-800 font-medium">
              {isDevelopmentMode ? 'Preparing training prompts...' : 'Training AI with your images...'}
            </p>
            {!isDevelopmentMode && (
              <p className="text-blue-600 text-sm mt-2">
                This may take 30-60 seconds. Please don't close this page.
              </p>
            )}
          </div>
        )}

        {/* Page Content and Image */}
        {!isTraining && storyPages.length > 0 && (
          <div className="space-y-6">
            {/* Page Content */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {slotLabel}
              </h2>
              <div className="bg-white p-4 rounded-lg border">
                <p className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {currentPageContent}
                </p>
              </div>
            </div>

            {/* Image Display / Editor */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-xl font-bold text-gray-800">Generated Illustration</h2>
                {!isEditingImage && getImageUrlForEditor() && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        showModal(
                          'confirm',
                          language === 'Hebrew' ? 'הסרת תמונה' : 'Remove picture',
                          language === 'Hebrew' ? 'להסיר את התמונה? תוכל להעלות תמונה חדשה אחר כך.' : 'Remove this image? You can upload a new one afterwards.',
                          handleRemoveImage,
                          language === 'Hebrew' ? 'הסר' : 'Remove'
                        );
                      }}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium text-sm transition"
                    >
                      Remove picture and upload a new one
                    </button>
                  </div>
                )}
              </div>
              
              {isGenerating ? (
                <div className="bg-white p-12 rounded-lg border text-center">
                  <LogoLoader size="md" className="mb-4" />
                  <p className="text-purple-800 font-medium">
                    {isDevelopmentMode ? 'Preparing prompt...' : 'Generating image...'}
                  </p>
                </div>
              ) : isEditingImage && !imageEditorCanLoad ? (
                <div className="bg-white p-8 rounded-lg border border-gray-200 text-center space-y-3">
                  <p className="text-gray-800 font-medium">
                    {language === 'Hebrew' ? 'טוען את העורך…' : 'Preparing the image editor…'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {language === 'Hebrew'
                      ? 'אם זה נמשך, רענן את הדף או צור מחדש את האיור.'
                      : 'If this stays empty, reload the page or regenerate the illustration.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsEditingImage(false)}
                    className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium"
                  >
                    {language === 'Hebrew' ? 'ביטול' : 'Cancel'}
                  </button>
                </div>
              ) : isEditingImage && imageEditorCanLoad ? (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <ImageEditor
                    key={`editor-${currentSlotIndex}-${imageRefreshKey}`}
                    imageUrl={getImageUrlForEditor(hasSavedEditedLayer)}
                    imageStorageKey={getStorageKeyForImageEditor(hasSavedEditedLayer)}
                    pageContent={currentIsSidePicture ? '' : currentPageContent}
                    allowSaveWithoutText={currentIsSidePicture}
                    textOverlay={(() => {
                      const isEditAgain = hasSavedEditedLayer;
                      const baseStyling = {
                        fontSize: sessionTextOverlayStyle.fontSize ?? 24,
                        color: sessionTextOverlayStyle.color ?? '#000000',
                        alignment: (sessionTextOverlayStyle.alignment ?? 'center') as 'left' | 'center' | 'right',
                        fontFamily: sessionTextOverlayStyle.fontFamily ?? 'Arial',
                        outlineColor: sessionTextOverlayStyle.outlineColor ?? '#FFFFFF',
                        outlineWidth: sessionTextOverlayStyle.outlineWidth ?? 2
                      };
                      // When editing an already-edited image we load the ORIGINAL (no text); keep overlay empty so user can add text again
                      if (isEditAgain) {
                        return {
                          ...baseStyling,
                          content: '',
                          position: { x: 0.5, y: 0.8 }
                        };
                      }
                      // Side pictures: no default text until user adds it
                      if (currentIsSidePicture) {
                        return {
                          ...baseStyling,
                          content: '',
                          position: { x: 0.5, y: 0.8 }
                        };
                      }
                      // Main images (first-time edit): use session styling with page content
                      const pageTextOverlay = currentPageImage?.textOverlay;
                      if (pageTextOverlay?.content) {
                        return {
                          ...baseStyling,
                          content: pageTextOverlay.content,
                          position: pageTextOverlay.position || { x: 0.5, y: 0.8 }
                        };
                      }
                      return {
                        ...baseStyling,
                        content: currentPageContent,
                        position: { x: 0.5, y: 0.8 }
                      };
                    })()}
                    onSave={handleSaveImageEdit}
                    onCancel={() => setIsEditingImage(false)}
                  />
                </div>
              ) : shouldShowSidePrompt ? (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-700">Illustration Prompt</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCopyCurrentPrompt}
                        className="px-3 py-1 bg-purple-600 text-white rounded text-sm"
                      >
                        {isCurrentPromptCopied ? '✅ Copied' : '📋 Copy Prompt'}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsIllustrationPromptExpanded((prev) => !prev)}
                    className="w-full rounded-lg border border-blue-200 bg-white px-4 py-3 text-left hover:bg-blue-50 transition-colors mb-3"
                    aria-expanded={isIllustrationPromptExpanded}
                    aria-controls="illustration-prompt-panel"
                  >
                    <span className="flex items-center justify-between">
                      <span>Complete Prompt</span>
                      <span>{isIllustrationPromptExpanded ? '▼' : '▶'}</span>
                    </span>
                  </button>
                  {isIllustrationPromptExpanded && (
                    <pre
                      id="illustration-prompt-panel"
                      className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 p-4 rounded max-h-96 overflow-y-auto"
                    >
                      {currentPagePrompt}
                    </pre>
                  )}
                  {/* In dev mode for side pictures, show a small preview of the MAIN page edited image (if any),
                      so it's clear what illustration the side background should continue from. */}
                  {isDevelopmentMode && currentIsSidePicture && getMainImageUrlForSidePreview() && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-600 mb-2">
                        Current side background image for this page:
                      </p>
                      <img
                        src={getMainImageUrlForSidePreview() || undefined}
                        alt=""
                        className="max-h-48 rounded-md shadow-sm border border-gray-200 mx-auto"
                      />
                    </div>
                  )}
                </div>
              ) : getImageUrlForEditor(false) && !isEditingImage ? (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="mb-4">
                    <img
                      key={`page-${currentSlotIndex}-${imageRefreshKey}`}
                      src={getImageUrlForEditor(false)}
                      alt=""
                      className="w-full rounded-lg shadow-md"
                      onError={() => {
                        console.error('ImageStudio: page illustration failed to load (URL omitted)');
                        setImageRefreshKey(prev => prev + 1);
                      }}
                    />
                  </div>
                  {currentPageImage?.approved && (
                    <div className="mb-3 p-2 bg-green-100 text-green-800 rounded text-sm text-center">
                      ✅ Approved
                    </div>
                  )}
                  {/* Show message if image is edited */}
                  {hasSavedEditedLayer && (
                    <div className="mb-2 p-2 bg-blue-50 text-blue-800 rounded text-sm text-center">
                      ✏️ Image with text overlay saved
                    </div>
                  )}
                  {/* Edit button - show if no edited image exists */}
                  {!hasSavedEditedLayer && (
                    <div className="text-center">
                      <button
                        onClick={() => setIsEditingImage(true)}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-lg transition"
                      >
                        ✏️ Edit Image & Add Text
                      </button>
                    </div>
                  )}
                </div>
              ) : isDevelopmentMode && currentPagePrompt && !isEditingImage ? (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-700">Illustration Prompt</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCopyCurrentPrompt}
                        className="px-3 py-1 bg-purple-600 text-white rounded text-sm"
                      >
                        {isCurrentPromptCopied ? '✅ Copied' : '📋 Copy Prompt'}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsIllustrationPromptExpanded((prev) => !prev)}
                    className="w-full rounded-lg border border-blue-200 bg-white px-4 py-3 text-left hover:bg-blue-50 transition-colors mb-3"
                    aria-expanded={isIllustrationPromptExpanded}
                    aria-controls="illustration-prompt-panel"
                  >
                    <span className="flex items-center justify-between">
                      <span>Complete Prompt</span>
                      <span>{isIllustrationPromptExpanded ? '▼' : '▶'}</span>
                    </span>
                  </button>
                  {isIllustrationPromptExpanded && (
                    <pre
                      id="illustration-prompt-panel"
                      className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 p-4 rounded max-h-96 overflow-y-auto"
                    >
                      {currentPagePrompt}
                    </pre>
                  )}
                </div>
              ) : (
                <div className="bg-white p-12 rounded-lg border text-center">
                  {isDevelopmentMode ? (
                    <div className="space-y-4">
                      <p className="text-gray-500 mb-4">No image uploaded yet for this page.</p>
                      <button
                        onClick={handleUploadImage}
                        disabled={isUploadingImage}
                        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold px-8 py-4 rounded-xl text-lg shadow-md transition transform hover:scale-105"
                      >
                        {isUploadingImage ? 'Uploading...' : '📤 Upload Image for This Page'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-500">No image generated yet. Click "Generate Image" to create one.</p>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 justify-center">
              {/* Dev Mode: Upload Image Button
                 - For main pictures: show only when no image exists
                 - For side pictures: always allow upload (background can be replaced freely) */}
              {isDevelopmentMode && !isGenerating && !isEditingImage && (
                (currentIsSidePicture || !getImageUrlForEditor()) && (
                <button
                  onClick={handleUploadImage}
                  disabled={isUploadingImage}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
                >
                  {isUploadingImage ? 'Uploading...' : '📤 Upload Image'}
                </button>
                )
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />

              {/* Production Mode: Generate Image Button */}
              {!isDevelopmentMode && !getImageUrlForEditor() && !currentPagePrompt && !isGenerating && (
                <button
                  onClick={() => generatePageImage(currentPageNumber, currentIsSidePicture)}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
                >
                  🎨 Generate Image
                </button>
              )}

              {(getImageUrlForEditor() || currentPagePrompt) && !isGenerating && !isEditingImage && !(
                isDevelopmentMode && currentIsSidePicture
              ) && (
                <>
                </>
              )}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={handlePreviousPage}
            disabled={currentSlotIndex === 0}
            className="bg-gray-500 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
          >
            ← Previous
          </button>

          <button
            onClick={handleNextPage}
            disabled={
              currentSlotIndex < totalSlots - 1 
                ? (!isCurrentPageApproved() && !isDevelopmentMode && !hasSavedEditedLayer)
                : false // On last slot, allow navigation to Cover Studio
            }
            className={
              currentSlotIndex === totalSlots - 1
                ? "bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
                : "bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
            }
          >
            {currentSlotIndex === totalSlots - 1 
              ? "Next: Cover Generation →" 
              : "Next →"}
          </button>
        </div>
      </div>

      {/* Navigation Buttons (Bottom) - positioned lower to avoid overlap */}
      <div className="absolute bottom-2 left-6 right-6 flex justify-between px-4 z-10">
        <button
          onClick={() => navigate("/create/story-studio")}
          className="bg-white/90 hover:bg-white text-heading border-2 border-brandNavy/30 font-semibold px-8 py-3 rounded-2xl text-lg shadow-soft hover:shadow-card transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
        >
          ← Back to Story
        </button>
      </div>
      </main>

      {/* Custom Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={modalState.onConfirm ? handleModalConfirm : undefined}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        icon={modalState.icon}
      />

      {batchModalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex min-h-0 items-center justify-center overflow-y-auto bg-transparent p-4"
            role="presentation"
          >
            <button
              type="button"
              className="fixed inset-0 z-0 m-0 border-0 bg-black/50 p-0 cursor-default"
              aria-label={t("imageStudio.batch.close")}
              onClick={() => setBatchModalOpen(false)}
            />
            <div
              role="dialog"
              aria-modal
              aria-labelledby="batch-prompt-modal-title"
              className="relative z-10 flex max-h-[min(90vh,100dvh)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
              dir={uiLocale === "he" ? "rtl" : "ltr"}
            >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-2">
              <h2 id="batch-prompt-modal-title" className="text-lg font-semibold text-gray-900">
                {t("imageStudio.batch.modalTitle")}
              </h2>
              <button
                type="button"
                onClick={() => setBatchModalOpen(false)}
                className="text-gray-500 hover:text-gray-800 px-2 py-1 rounded"
              >
                {t("imageStudio.batch.close")}
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
              <p className="text-sm text-gray-600">{t("imageStudio.batch.hintMax")}</p>
              <p className="text-sm text-gray-600">
                {t("imageStudio.batch.hintRange", { max: storyPages.length || 1 })}
              </p>
              <div className="flex flex-wrap gap-4 items-end">
                <label className="flex flex-col text-sm text-gray-700 gap-1">
                  {t("imageStudio.batch.startPage")}
                  <input
                    type="number"
                    min={1}
                    max={storyPages.length || 1}
                    value={batchStartStr}
                    onChange={(e) => setBatchStartStr(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-24"
                  />
                </label>
                <label className="flex flex-col text-sm text-gray-700 gap-1">
                  {t("imageStudio.batch.endPage")}
                  <input
                    type="number"
                    min={1}
                    max={storyPages.length || 1}
                    value={batchEndStr}
                    onChange={(e) => setBatchEndStr(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 w-24"
                  />
                </label>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-gray-800 mb-2">
                  {t("imageStudio.batch.slotKindLabel")}
                </legend>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="batch-slot-kind"
                    checked={batchMainOrSide === "main"}
                    onChange={() => setBatchMainOrSide("main")}
                  />
                  {t("imageStudio.batch.slotMain")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="batch-slot-kind"
                    checked={batchMainOrSide === "side"}
                    onChange={() => setBatchMainOrSide("side")}
                  />
                  {t("imageStudio.batch.slotSide")}
                </label>
              </fieldset>
              {batchModalError && (
                <p className="text-sm text-red-600" role="alert">
                  {batchModalError}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={buildBatchPrompt}
                  disabled={batchLoading}
                  className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium"
                >
                  {batchLoading ? t("imageStudio.batch.building") : t("imageStudio.batch.build")}
                </button>
                {batchBuiltText && (
                  <button
                    type="button"
                    onClick={handleCopyBatchBuiltPrompt}
                    className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium"
                  >
                    {isBatchPromptCopied ? t("imageStudio.batch.copied") : t("imageStudio.batch.copyPrompt")}
                  </button>
                )}
              </div>
              {batchBuiltText && (
                <div>
                  <p className="text-sm font-medium text-gray-800 mb-2">{t("imageStudio.batch.previewLabel")}</p>
                  <pre className="whitespace-pre-wrap text-xs text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200 max-h-64 overflow-y-auto">
                    {batchBuiltText}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>,
          document.body
        )}
    </div>
  );
};

export default ImageStudio;

